import React from "react";
import { FaEdit, FaTrash } from "react-icons/fa";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import styled, { css } from "styled-components";

import ActionConfirmationButton from "../../components/pages/CourseAuthor/ActionConfirmation";
import { Link } from "../../components/pages/utils";
import { Header as H1 } from "../../components/utils/Headings";
import { usePagination } from "../../components/utils/Pagination/hooks";
import {
  ActionRow,
  BottomPagination,
  Count,
  TopPagination,
} from "../../components/utils/Pagination/NumberedPagination/common";
import { Table } from "../../components/utils/Pagination/NumberedPagination/table";
import { SideMenu } from "../../components/utils/SideMenu/SideMenu.container";
import { SideMenuArrowContext, SideMenuCloseArrow } from "../../components/utils/SideMenu/SideMenuCloseArrow";
import Spinner from "../../components/utils/Spinner";
import SubTopbar from "../../components/utils/SubTopbar";
import TopBar from "../../components/utils/TopBar";
import { PageContextProvider } from "../../context/numbered-pagination";
import * as selectors from "../../selectors/selectors";
import { pluralize } from "../../utils";
import { IState } from "../reducer";
import { IScormPackage, RequestState } from "../types/common";
import * as actions from "./actions";
import { OpenPackageIcon, ClosedPackageIcon } from "../../components/utils/Icons/PackageIcon";

interface IExportedScormPackagesStateProps {
  organizationName: string;
  isLoading: boolean;
  scormPackages: IScormPackage[];
  userName: string;
}

interface IExportedScormPackagesDispatchProps {
  deleteScormPackage: (scormPackageId: string) => () => void;
  fetchScormPackages: () => void;
  mount: () => void;
  unmount: () => void;
}

type IExportedScormPackagesProps = IExportedScormPackagesStateProps & IExportedScormPackagesDispatchProps;

const Container = styled.div`
  ${({ theme: { primaryTheme } }) => `
    width: 100%;

    h1 {
      font-size: ${primaryTheme.h1FontSize};
      font-weight: ${primaryTheme.h1FontWeight};
    }`}
`;

const CustomActionRow = styled(ActionRow)`
  flex-direction: row;
`;

const Placeholder = styled.div`
  width: 16rem;
`;

const Name = styled.div`
  margin: 0 1rem;
`;

const ScormPackagesContainer = styled(Container)`
  margin-top: 0.75em;
`;

const fixedSize = css`
  text-align: center;
  width: 10rem;
`;

interface IScormPackagePagination {
  deleteScormPackage: (scormPackageId: string) => () => void;
  packages: IScormPackage[];
}

interface IScormPackageRow {
  name: React.ReactNode;
  actions: React.ReactNode;
}

const ScormPackagePagination: React.FC<IScormPackagePagination> = ({ packages, deleteScormPackage }) => {
  const { value, pagedData } = usePagination(packages);

  const total = packages.length;
  const toScormPackageRow = ({ name, scormPackageId, state }: IScormPackage): IScormPackageRow => ({
    actions: (
      <ActionConfirmationButton action="Delete" actionFn={deleteScormPackage(scormPackageId)} ActionIcon={FaTrash} />
    ),
    name: (
      <Link to={`/scorm-packages/${scormPackageId}`}>
        {state === "closed" ? <ClosedPackageIcon /> : <OpenPackageIcon />}
        <Name>{name}</Name>
        <FaEdit />
      </Link>
    ),
  });

  return (
    <PageContextProvider value={value}>
      <CustomActionRow>
        <Placeholder />
        <TopPagination total={total} />
        <Count>{pluralize("Scorm Package", packages.length)}</Count>
      </CustomActionRow>
      <Table
        columns={[
          { name: "Name", accessor: "name" },
          { name: "Actions", accessor: "actions", styles: fixedSize },
        ]}
        data={pagedData.map(toScormPackageRow)}
      />
      <BottomPagination total={total} />
    </PageContextProvider>
  );
};

const ExportedScormPackagesComponent: React.FC<IExportedScormPackagesProps> = props => {
  const { organizationName, userName } = props;

  const [menuExpanded, setMenuExpanded] = React.useState(false);

  const { isOpen } = React.useContext(SideMenuArrowContext);

  const onToggleMenuClick = () => setMenuExpanded(!menuExpanded);

  React.useEffect(() => {
    props.mount();
    props.fetchScormPackages();
    return props.unmount;
  }, []);

  if (props.isLoading) {
    return <Spinner />;
  }

  return (
    <>
      <TopBar showToggleMenu={true} onToggleMenuClick={onToggleMenuClick} userName={userName} />
      <SideMenu
        expanded={menuExpanded}
        organizationName={organizationName}
        userName={userName}
        onClose={onToggleMenuClick}
        isOpen={isOpen}
      />
      <SideMenuCloseArrow />
      <SubTopbar withSidebar={true} isOpen={isOpen}>
        <H1>SCORM Packages</H1>
        <ScormPackagesContainer>
          <ScormPackagePagination packages={props.scormPackages} deleteScormPackage={props.deleteScormPackage} />
        </ScormPackagesContainer>
      </SubTopbar>
    </>
  );
};

const mapStateToProps = (state: IState): IExportedScormPackagesStateProps => ({
  isLoading:
    state.App.ExportedScormPackages.fetchScormPackagesStatus === RequestState.PENDING ||
    state.App.ExportedScormPackages.deleteScormPackageRequestState === RequestState.PENDING,
  organizationName: selectors.organizationName(state),
  scormPackages: selectors.exportedScormPackages(state),
  userName: selectors.accountName(state),
});

const mapDispatchToProps = (dispatch: Dispatch): IExportedScormPackagesDispatchProps => ({
  deleteScormPackage: (scormPackageId: string) => () => dispatch(actions.deleteScormPackage(scormPackageId)),
  fetchScormPackages: () => dispatch(actions.fetchExportedScormPackages()),
  mount: () => dispatch(actions.mount()),
  unmount: () => dispatch(actions.unmount()),
});

export const ExportedScormPackages = connect(mapStateToProps, mapDispatchToProps)(ExportedScormPackagesComponent);
